/** The quick actions a launch policy offers — shared by the grid's "In use"
 *  cell and the edit dialog's title, so both answer the same question the same
 *  way: WHICH tours is this policy on?
 *
 *  `in_use_count` counts tours plus the tenants that default to the policy;
 *  only the tours half is reachable, because /tour-operators has no
 *  `launch_policy` namespace. The count and the link therefore disagree by the
 *  tenant defaults, and the labels say "tours" rather than "records".
 *
 *  Never `/launch-policies?edit=<id>`: the page has no edit intent and such a
 *  link lands on the unfiltered list.
 */
import { operatorActions, type OperatorRef } from '@/components/ValueActions/identity';
import { act, type ValueAction } from '@/components/ValueActions/ValueActions';
import { idCond, launchPoliciesHref, toursHref } from '@/utils/gridLinks';

/** The tours this policy is set on. The `launch_policy` namespace declares
 *  both exact and iexact, so the id-match form pins THIS policy rather than
 *  every same-named one (policy names repeat across tenants). */
export function launchPolicyActions(
  policy: { id: number; name: string },
  tenantId?: number | null,
): ValueAction[] {
  return [
    act.filter(toursHref([idCond('launch_policy', policy.id)], tenantId ?? null), 'Tours on this policy'),
    act.copy(String(policy.id), 'Copy policy id'),
  ];
}

/** The operator a policy belongs to: its page, and its other policies. */
export function launchPolicyOperatorActions(tenant: OperatorRef): ValueAction[] {
  return operatorActions(tenant, {
    href: launchPoliciesHref({ tenant: tenant.id }),
    label: 'Launch policies of this operator',
  });
}
